/**
 * WebSocket Service - Singleton WebSocket connection manager
 */

import { WS_URL, WS_RECONNECT_DELAY, WS_MAX_RECONNECT_ATTEMPTS, CONNECTION_STATUS } from '../config/constants';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.url = WS_URL;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = WS_MAX_RECONNECT_ATTEMPTS;
    this.reconnectDelay = WS_RECONNECT_DELAY;
    this.status = CONNECTION_STATUS.DISCONNECTED;
    this.messageQueue = [];
    this.statusListeners = new Set();
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      console.log('[WebSocket] Already connected');
      return;
    }

    this.updateStatus(CONNECTION_STATUS.CONNECTING);
    console.log('[WebSocket] Connecting to', this.url);

    try {
      const socket = new WebSocket(this.url);
      this.ws = socket;

      socket.onopen = () => {
        if (this.ws !== socket) return; // stale socket
        console.log('[WebSocket] Connected');
        this.updateStatus(CONNECTION_STATUS.CONNECTED);
        this.reconnectAttempts = 0;

        // Send queued messages
        this.flushMessageQueue(socket);
      };

      socket.onmessage = (event) => {
        if (this.ws !== socket) return; // stale socket
        try {
          const message = JSON.parse(event.data);
          console.log('[WebSocket] Message received:', message.type || message.action);

          // Route message to registered listeners
          this.routeMessage(message);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      socket.onclose = (event) => {
        if (this.ws !== socket) return; // stale socket
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        this.updateStatus(CONNECTION_STATUS.DISCONNECTED);

        // Attempt to reconnect
        this.attemptReconnect();
      };

      socket.onerror = (error) => {
        if (this.ws !== socket) return; // stale socket
        console.error('[WebSocket] Error:', error);
        this.updateStatus(CONNECTION_STATUS.FAILED);
      };
    } catch (error) {
      console.error('[WebSocket] Failed to create connection:', error);
      this.updateStatus(CONNECTION_STATUS.FAILED);
      this.attemptReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    console.log('[WebSocket] Disconnecting');
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.updateStatus(CONNECTION_STATUS.DISCONNECTED);
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WebSocket] Max reconnection attempts reached');
      this.updateStatus(CONNECTION_STATUS.FAILED);
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    this.updateStatus(CONNECTION_STATUS.RECONNECTING);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Send message to server
   */
  send(action, data = {}) {
    const message = { action, ...data };

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Sending:', action);
      try {
        this.ws.send(JSON.stringify(message));
      } catch (error) {
        // If send fails due to a stale socket, re-queue and retry on next open.
        console.warn('[WebSocket] Send failed, queueing message:', action, error);
        this.messageQueue.push(message);
      }
    } else {
      console.log('[WebSocket] Queueing message (not connected):', action);
      this.messageQueue.push(message);
    }
  }

  /**
   * Flush queued messages
   */
  flushMessageQueue(socket = this.ws) {
    if (this.messageQueue.length === 0) return;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    console.log(`[WebSocket] Flushing ${this.messageQueue.length} queued messages`);

    while (this.messageQueue.length > 0 && socket.readyState === WebSocket.OPEN) {
      const message = this.messageQueue[0];
      try {
        socket.send(JSON.stringify(message));
        this.messageQueue.shift();
      } catch (error) {
        console.warn('[WebSocket] Failed to flush queued message, will retry on reconnect', error);
        break;
      }
    }
  }

  /**
   * Subscribe to message type
   */
  subscribe(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }

    this.listeners.get(type).add(callback);

    console.log(`[WebSocket] Subscribed to '${type}' (${this.listeners.get(type).size} listeners)`);

    // Return unsubscribe function
    return () => {
      this.unsubscribe(type, callback);
    };
  }

  /**
   * Unsubscribe from message type
   */
  unsubscribe(type, callback) {
    if (this.listeners.has(type)) {
      this.listeners.get(type).delete(callback);

      if (this.listeners.get(type).size === 0) {
        this.listeners.delete(type);
      }

      console.log(`[WebSocket] Unsubscribed from '${type}'`);
    }
  }

  /**
   * Route message to listeners
   */
  routeMessage(message) {
    const type = message.type || message.action;

    if (!type) {
      // Two benign cases arrive without a `type`:
      // 1. API Gateway error frame: { message: "Internal server error", connectionId, requestId }
      // 2. Lambda return-body echo: { message: "Home feed retrieved" } (route response integration)
      // Only log case 1 (has connectionId = real API GW error). Silently drop case 2.
      if (message.connectionId) {
        console.error('[WebSocket] API Gateway error:', message.message);
      }
      return;
    }

    if (typeof type !== 'string') {
      console.warn('[WebSocket] Dropping message with non-string type:', typeof type);
      return;
    }

    if (message.data !== undefined && (message.data === null || typeof message.data !== 'object' || Array.isArray(message.data))) {
      console.warn('[WebSocket] Dropping message with invalid data shape for type:', type);
      return;
    }

    const listeners = this.listeners.get(type);

    if (listeners && listeners.size > 0) {
      listeners.forEach(callback => {
        try {
          callback(message.data || message);
        } catch (error) {
          console.error(`[WebSocket] Listener error for '${type}':`, error);
        }
      });
    }
  }

  /**
   * Subscribe to connection status changes
   */
  onStatusChange(callback) {
    this.statusListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  /**
   * Update connection status
   */
  updateStatus(newStatus) {
    if (this.status === newStatus) return;

    console.log(`[WebSocket] Status: ${this.status} -> ${newStatus}`);
    this.status = newStatus;

    // Notify status listeners
    this.statusListeners.forEach(callback => {
      try {
        callback(newStatus);
      } catch (error) {
        console.error('[WebSocket] Status listener error:', error);
      }
    });
  }

  /**
   * Get current connection status
   */
  getStatus() {
    return this.status;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.status === CONNECTION_STATUS.CONNECTED;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
